import { useEffect, useState } from "react";
import { Box, Button, Divider, TextField, Typography } from "@mui/material";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";

import AppDialog from "../General/Dialog/AppDialog";
import PartyAutocomplete from "./PartyAutocomplete";
import {
  createClient,
  updateClient,
  attachPartyToClient,
} from "../../services/ClientService";
import { useToast } from "../../context/ToastContext";

const emptyForm = { name: "", document: "" };

export default function ClientFormDialog({ open, onClose, editData, onSaved }) {
  const { showToast } = useToast();
  const [form, setForm] = useState(emptyForm);
  const [parties, setParties] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(
        editData
          ? { name: editData.name || "", document: editData.document || "" }
          : emptyForm,
      );
      setParties([]);
      setErrors({});
    }
  }, [open, editData]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleDocumentChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, "");
    setForm((prev) => ({ ...prev, document: digitsOnly }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setErrors({});
    try {
      if (editData) {
        await updateClient(editData.id, form);
        showToast("Cliente actualizado correctamente.", "success");
      } else {
        const res = await createClient(form);
        const newClient = res.data?.data;

        if (newClient?.id && parties.length) {
          await Promise.all(
            parties.map((p) => attachPartyToClient(newClient.id, p.id)),
          );
        }

        showToast(
          parties.length
            ? `Cliente creado y ${parties.length} parte(s) vinculada(s).`
            : "Cliente creado correctamente.",
          "success",
        );
      }
      onSaved();
    } catch (err) {
      if (err.response?.status === 422) {
        setErrors(err.response.data?.errors || {});
      } else {
        showToast(
          err.response?.data?.message || "No se pudo guardar el cliente.",
          "error",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={editData ? "Editar cliente" : "Nuevo cliente"}
      subtitle={
        editData
          ? "Actualizá los datos del cliente."
          : "Registrá un cliente y vinculá de una vez las partes que le correspondan."
      }
      icon={<BadgeRoundedIcon />}
      actions={
        <>
          <Button onClick={onClose} disabled={saving} sx={{ borderRadius: 2, fontWeight: 800 }}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving || !form.name.trim()}
            sx={{ borderRadius: 2, fontWeight: 900 }}
          >
            {editData ? "Guardar" : "Crear"}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 0.5 }}>
        <TextField
          label="Nombre / Razón social"
          value={form.name}
          onChange={handleChange("name")}
          error={Boolean(errors.name)}
          helperText={errors.name?.[0]}
          fullWidth
          autoFocus
        />
        <TextField
          label="Documento (CUIT)"
          value={form.document}
          onChange={handleDocumentChange}
          error={Boolean(errors.document)}
          helperText={errors.document?.[0] || "Solo números, sin guiones ni espacios."}
          slotProps={{ htmlInput: { inputMode: "numeric", pattern: "[0-9]*" } }}
          fullWidth
        />

        {!editData && (
          <>
            <Divider sx={{ opacity: 0.6 }} />
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 14, mb: 0.8 }}>
                Vincular partes (opcional)
              </Typography>
              <PartyAutocomplete value={parties} onChange={setParties} />
            </Box>
          </>
        )}
      </Box>
    </AppDialog>
  );
}
